import type { ApiAxis, ApiProduct, ApiSku } from "./api";
export type Selection = Record<string, string>;

/**
 * Where a configurator opens: on the first buyable variant, never on
 * "nothing". Shared by the product page and the quick-add sheet, so a card and
 * its page agree on what "the default" is.
 */
export function initialSelection(product: Pick<ApiProduct, "skus">): Selection {
  const skus = product.skus ?? [];
  const first = skus.find((s) => s.isActive && !s.soldOut) ?? skus[0];
  return { ...(first?.options ?? {}) };
}
export function matches(s: ApiSku, selection: Selection) {
  return (
    Object.keys(s.options ?? {}).length === Object.keys(selection).length &&
    Object.entries(selection).every(([id, v]) => s.options?.[id] === v)
  );
}
/**
 * Whether a choice should render struck through and unpickable.
 *
 * Inquiry-only values are never disabled, whatever the SKU list says. They have
 * no SKU behind them by design — nothing is minted for "Custom" — so the
 * buyable-combination test below concludes there is nothing to sell, which is
 * true and is exactly not the point. Disabling it would label a made-to-order
 * desk "Habis".
 */
export function optionDisabled(
  skus: ApiSku[],
  selection: Selection,
  axis: string,
  value: string,
  axes: ApiAxis[] = [],
) {
  if (isInquiryOnly(axes, axis, value)) return false;
  const buyable = skus.filter((s) => s.isActive && !s.soldOut);
  const other = Object.entries(selection).filter(([id]) => id !== axis);
  const exact = buyable.filter((s) =>
    other.every(([id, v]) => s.options?.[id] === v),
  );
  return !(exact.length ? exact : buyable).some(
    (s) => s.options?.[axis] === value,
  );
}

/** Whether one value on one axis is a consultation rather than a purchase. */
export function isInquiryOnly(axes: ApiAxis[], axisId: string, valueId: string) {
  const axis = axes.find((a) => a.optionTypeId === axisId);
  return Boolean(axis?.optionType.values.find((v) => v.id === valueId)?.inquiryOnly);
}

/**
 * The inquiry-only values the shopper currently has selected.
 *
 * Returned as labels rather than a boolean because the WhatsApp message names
 * them: "Custom" on its own tells the admin nothing, and a shopper who picked
 * a custom size AND a custom finish is asking about both.
 */
export function inquirySelections(axes: ApiAxis[], selection: Selection): string[] {
  return axes.flatMap((a) => {
    const id = selection[a.optionTypeId];
    if (!id) return [];
    const value = a.optionType.values.find((v) => v.id === id);
    return value?.inquiryOnly ? [`${a.optionType.label}: ${value.label}`] : [];
  });
}

/** The label of the value chosen on one axis, if any. */
export function selectedLabel(axis: ApiAxis, selection: Selection): string | undefined {
  const id = selection[axis.optionTypeId];
  return id ? axis.optionType.values.find((v) => v.id === id)?.label : undefined;
}

/**
 * The chosen label on the axis playing a given role ("top", "leg", "size").
 *
 * The photo ladder is keyed on colour *names*, so this is the bridge from the
 * CMS selection (option-value ids) to the gallery.
 */
export function labelFor(axes: ApiAxis[], role: string, selection: Selection): string | undefined {
  const axis = axes.find((a) => a.optionType.role === role);
  return axis ? selectedLabel(axis, selection) : undefined;
}
